import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useFetchGroup } from '../../hooks/fetch-group';
import { Button, Typography, Box, Tab, Tabs, Badge } from '@mui/material';
import { joinGroup, getGroupComments } from '../../services/group-services';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/useAuth';
import Comments from '../comments/comments';
import EventList from '../events/event-list';
import EventTimeline from '../events/event-timeline';
import ViewListIcon from '@mui/icons-material/ViewList';
import TimelineIcon from '@mui/icons-material/Timeline';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MemberList from './member-list';


function GroupDetails() {

    const { authData } = useAuth();
    const { id } = useParams();
    const [refreshKey, setRefreshKey] = useState(0);
    const [data, loading, error] = useFetchGroup(id, authData.token, refreshKey);
    const [group, setGroup] = useState(null);
    const [isGroup, setInGroup] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const prefKey = `groupPrefs_${id}`;
    const loadPref = (key, fallback) => localStorage.getItem(`${prefKey}_${key}`) || fallback;

    const [tabValue, setTabValue] = useState(() => loadPref('tab', 'members'));
    const [filter, setFilter] = React.useState(() => loadPref('filter', 'future'));
    const [filteredEvents, setFilteredEvents] = React.useState([]);
    const [viewMode, setViewMode] = useState(() => loadPref('view', 'timeline'));
    const [unseenComments, setUnseenComments] = useState(0);

    const storageKey = `lastSeenComment_${id}_${authData.user.id}`;

    const getLastSeenId = () => parseInt(localStorage.getItem(storageKey) || '0', 10);

    const handleAlignment = (event, newFilter) => {
        setFilter(newFilter);
        localStorage.setItem(`${prefKey}_filter`, newFilter);

        const currentTime = new Date();
        let newEvents = group.events;
        if (newFilter === "future") {
            newEvents = group.events.filter(evt => currentTime < new Date(evt.time));
        } else if (newFilter === "past") {
            newEvents = group.events.filter(evt => currentTime > new Date(evt.time));
        }
        setFilteredEvents(newEvents.sort((a, b) => new Date(a.time) - new Date(b.time)));
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        localStorage.setItem(`${prefKey}_tab`, newValue);
        if (newValue === 'comments') {
            // The Comments component persists the latest-seen id on mount;
            // clear the badge immediately for snappy feedback.
            setUnseenComments(0);
        }
    };

    const navigate = useNavigate();

    useEffect(() => {
        if (data?.members) {

            data.members.sort((a, b) => b.points - a.points);

            const trophyForPosition = { 1: 'gold', 2: 'silver', 3: 'bronze' };
            let position = 0;

            data.members.forEach((m, indx) => {
                // Competition ranking (1, 2, 2, 4): tied members share the rank of
                // the first in their group; the next distinct score jumps to its
                // true 1-based position, skipping the ranks consumed by the tie.
                if (indx === 0 || m.points !== data.members[indx - 1].points) {
                    position = indx + 1;
                }
                m.position = position;
                m.trophy = trophyForPosition[position];
            });

            if (authData?.user) {
                setInGroup(!!data.members.find(member => member.user.id === authData.user.id));
                setIsAdmin(data.members.find(member => member.user.id === authData.user.id)?.admin);
            }
        }
        setGroup(data);
        if (data && data.events) {
            const currentTime = new Date();
            let newEvents = data.events;
            if (filter === 'future') newEvents = data.events.filter(evt => currentTime < new Date(evt.time));
            else if (filter === 'past') newEvents = data.events.filter(evt => currentTime > new Date(evt.time));
            setFilteredEvents(newEvents.sort((a, b) => new Date(a.time) - new Date(b.time)));
        }
    }, [data, authData, filter])

    // Fetch only the unseen-comment count for the badge — keeps the group
    // payload small (full comments load lazily in the Comments tab).
    useEffect(() => {
        if (!id || !authData?.token) return;
        getGroupComments(authData.token, id, { limit: 0, lastSeen: getLastSeenId() })
            .then((res) => setUnseenComments(res.unseen))
            .catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, authData?.token, refreshKey])

    // Joining is closed once the first event has kicked off
    const hasStartedEvents = !!group?.events?.some(evt => new Date(evt.time) <= new Date());

    const joinHere = () => {
        joinGroup({ user: authData.user.id, group: group.id })
            .then(() => {
                toast.success('You have joined the group!');
                setRefreshKey(k => k + 1);
            })
            .catch(err => {
                toast.error(err.message || 'Failed to join the group. Please try again.');
            });
    }

    const addEvent = () => {
        navigate('/event-form', { state: { group } })
    }

    if (error) return <Typography variant="h1" sx={{ textAlign: 'center', mt: 4 }}>Error</Typography>
    if (loading) return <Typography variant="h2" sx={{ textAlign: 'center', mt: 4 }}>Loading…</Typography>


    return (
        <div>
            {group &&
                <TabContext value={tabValue}>
                    <Box display="flex" alignItems="center" mb={group.description ? 1 : 3}>
                        <Link to={`/`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#ff9800', background: 'rgba(255,152,0,0.10)', border: '1px solid rgba(255,152,0,0.25)', borderRadius: 20, padding: '5px 12px 5px 6px', marginRight: '1rem', textDecoration: 'none', fontWeight: 600, fontSize: 13 }}>
                            <ChevronLeftIcon sx={{ fontSize: 18 }} />
                            Back
                        </Link>
                        <Typography variant="h2" sx={{ flexGrow: 1 }}>{group.name}</Typography>
                        {isGroup ?
                            <div>
                                {isAdmin && tabValue === "events" &&
                                    <Button onClick={() => addEvent()} variant="contained" color="primary" size="small">
                                        Add Event
                                    </Button>}
                            </div>
                            :
                            !hasStartedEvents &&
                            <Button onClick={() => joinHere()} variant="contained" color="primary" size="small">
                                Join Group
                            </Button>
                        }
                    </Box>

                    {group.description && (
                        <Typography sx={{
                            fontSize: 13, color: '#b0b0b0', mb: 2, textAlign: 'left',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                            {group.description}
                        </Typography>
                    )}

                    <Box sx={{ borderBottom: 1, borderColor: 'rgba(255,255,255,0.1)' }} mb={2}>
                        <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth"
                            indicatorColor="primary" textColor="primary">
                            <Tab label="Members" value="members" sx={{ textTransform: 'none', fontWeight: 600 }} />
                            <Tab label="Events" value="events" sx={{ textTransform: 'none', fontWeight: 600 }} />
                            <Tab value="comments" sx={{ textTransform: 'none', fontWeight: 600 }} label={
                                <Badge badgeContent={unseenComments} color="error" sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}>
                                    <Box sx={{ pr: unseenComments > 0 ? 1.5 : 0 }}>Comments</Box>
                                </Badge>
                            } />
                        </Tabs>
                    </Box>
                    <TabPanel value="members" sx={{ p: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, px: 0.5 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Standings</Typography>
                            <Typography sx={{ fontSize: 11, color: '#b0b0b0' }}>{group.members.length} players</Typography>
                        </Box>
                        <MemberList members={group.members} currentUserId={authData?.user?.id} groupId={group.id} />
                    </TabPanel>
                    <TabPanel value="events">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                            <ToggleButtonGroup
                                value={filter}
                                exclusive
                                onChange={handleAlignment}
                                aria-label="event filter"
                                size="small"
                            >
                                <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.5, py: 0.5, fontSize: 12, fontWeight: 600 }}>All</ToggleButton>
                                <ToggleButton value="future" sx={{ textTransform: 'none', px: 1.5, py: 0.5, fontSize: 12, fontWeight: 600 }}>Upcoming</ToggleButton>
                                <ToggleButton value="past" sx={{ textTransform: 'none', px: 1.5, py: 0.5, fontSize: 12, fontWeight: 600 }}>Past</ToggleButton>
                            </ToggleButtonGroup>

                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={(_, v) => { if (v) { setViewMode(v); localStorage.setItem(`${prefKey}_view`, v); } }}
                                aria-label="view mode"
                                size="small"
                            >
                                <ToggleButton value="list" sx={{ px: 1 }}><ViewListIcon sx={{ fontSize: 18 }} /></ToggleButton>
                                <ToggleButton value="timeline" sx={{ px: 1 }}><TimelineIcon sx={{ fontSize: 18 }} /></ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        {viewMode === 'list'
                            ? <EventList events={filteredEvents} />
                            : <EventTimeline events={filteredEvents} />
                        }
                    </TabPanel>
                    <TabPanel value="comments">
                        <Comments group={group} onSeen={() => setUnseenComments(0)} />
                    </TabPanel>
                </TabContext>
            }

        </div>
    );
}

export default GroupDetails;
